import type { Pagination } from '@aigc/types';
import { endOfDay, startOfDay, subDays, subHours } from 'date-fns';
import type { VideoModelCode } from '../video-model/model-spec';

/**
 * 时间范围预设标签
 */
export const timeRangeMap = new Map<string, string>([
  ['last_1_hours', '最近1小时'],
  ['last_6_hours', '最近6小时'],
  ['last_12_hours', '最近12小时'],
  ['today', '今天'],
  ['yesterday', '昨天'],
  ['three_days', '最近3天'],
  ['week', '最近一周'],
]);

/**
 * DateRangePicker 预设值，对应后端 get_statistics_date_range() 逻辑
 */
export const TIME_RANGE_PRESETS = [
  {
    label: '最近1小时',
    value: (): [Date, Date] => [subHours(Date.now(), 1), new Date()],
  },
  {
    label: '最近6小时',
    value: (): [Date, Date] => [subHours(Date.now(), 6), new Date()] as const,
  },
  {
    label: '最近12小时',
    value: (): [Date, Date] => [subHours(Date.now(), 12), new Date()] as const,
  },
  {
    label: '今天',
    value: (): [Date, Date] =>
      [startOfDay(Date.now()), endOfDay(Date.now())] as const,
  },
  {
    label: '昨天',
    value: (): [Date, Date] => {
      const yesterday = subDays(Date.now(), 1);
      return [startOfDay(yesterday), endOfDay(yesterday)];
    },
  },
  {
    label: '最近3天',
    value: (): [Date, Date] => {
      const yesterday = subDays(Date.now(), 1);
      return [startOfDay(subDays(yesterday, 2)), endOfDay(yesterday)];
    },
  },
  {
    label: '最近一周',
    value: (): [Date, Date] => {
      const yesterday = subDays(Date.now(), 1);
      return [startOfDay(subDays(yesterday, 6)), endOfDay(yesterday)];
    },
  },
];

/**
 * 任务统计数据查询参数
 */
export interface TaskStatisticsInputDto {
  start_time: number;
  end_time: number;
}

/**
 * 任务统计数据
 */
export interface TaskStatisticsOutputDto {
  /**
   * 主键 ID
   */
  id: string;
  /**
   * 时间范围
   */
  range_time: [startTime: number, endTime: number];
  /**
   * 账户统计信息
   */
  account_stats: {
    /**
     * 成功数量
     */
    succeeded: number;
    /**
     * 失败数量
     */
    failed: number;
    /**
     * 其他状态
     */
    other: number;
    /**
     * 用户 ID
     *
     * @deprecated
     */
    account_id: string;
    /**
     * 用户 ID
     */
    created_by?: string;
    /**
     * 用户名称
     */
    account_name: string;
    /**
     * 当前每日信用点额度；-1=无限额度，0=无额度
     */
    credit: number;
    /**
     * 脚本数量
     */
    script_count: number;
    /**
     * 图片生成数量
     */
    image: number;
    /**
     * 信用点使用量
     */
    cost: number;
  }[];
  /**
   * 模型统计信息
   */
  model_stats: {
    /**
     * 成功数量
     */
    succeeded: number;
    /**
     * 失败数量
     */
    failed: number;
    /**
     * 其他状态
     */
    other: number;
    /**
     * 模型代码
     */
    model: VideoModelCode;
    /**
     * 信用点使用量
     */
    cost: number;
  }[];
  department_stats: {
    department: string;
    video_count: number;
  }[];
  /**
   * 视频数量
   */
  videos: number;
  /**
   * 图片生成数量
   */
  images: number;
  /**
   * 脚本数量
   */
  scripts: number;
  /**
   * 信用点使用总量
   */
  cost: number;
}

/**
 * 用户信用点额度查询参数（对应后端 UserStatisticsRequest）
 */
export interface UserCreditsQueryInput {
  pagination: Pagination;
  /**
   * 用户名模糊匹配关键词，支持用户前缀（如 dev、GX）
   */
  keyword?: string;
}

/**
 * 用户信用点额度（对应后端 UserStatistics，仅取 name + credit）
 */
export interface UserCreditItem {
  /**
   * 账户 ID
   */
  id: string;
  /**
   * 账户名称
   */
  name: string;
  /**
   * 展示名称
   */
  display_name?: string | null;
  /**
   * 每日信用点额度；0=无额度，-1=无限额度，正整数=每日额度
   */
  credit: number;
}

/**
 * 更新用户信用点额度入参（对应后端 UpdateUserCreditRequest）
 */
export interface UpdateUserCreditInput {
  account_name: string;
  credit: number;
}
